import type { UserRole } from '@/features/auth/types';
import type { TrustedAdminRole } from '@/features/auth/staffIdentity';
import type { UserDetails } from '@/features/users/types';
import { adminApi } from '@/config/api';
import { apiGet } from './httpClient';
import { mapUser, type RawUser } from './userMapper';
import { mapPage, type Page, type RawPage } from './page';

/**
 * GET /init — вызывается один раз после входа.
 * Отдаёт вошедшего (`me`) и первую страницу списка пользователей (`users`).
 */

interface RawInitResponse {
  me?: RawUser;
  users?: RawPage<RawUser> | RawUser[];
}

export interface InitIdentity {
  id: string;
  /** Имя и фамилия; если пусты — телефон, чтобы в шапке было хоть что-то. */
  name: string;
  /** Исходные значения: по ним видно, заполнил ли человек профиль. */
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  /** Роли как их прислал сервер. */
  rawRoles: string[];
  role: UserRole;
  /** Роль персонала, от которой считаются права. null — прав в кабинете нет. */
  trustedRole: TrustedAdminRole | null;
  balance: number | null;
}

export interface InitData {
  identity: InitIdentity;
  users: Page<UserDetails>;
}

const ROLE_BY_SERVER_TITLE: Record<string, UserRole> = {
  administrator: 'ceo',
  admin: 'ceo',
  ceo: 'ceo',
  manager: 'manager',
  support: 'support',
  operator: 'operator',
  owner: 'owner',
  user: 'client',
};

let warnedAboutRole = false;

/**
 * Берём самую широкую из присланных ролей: сервер отдаёт массив.
 * Неизвестная роль правами не наделяет — это `client`, доступа в кабинет нет.
 */
function mapRole(titles: string[]): UserRole {
  const known = titles
    .map((title) => ROLE_BY_SERVER_TITLE[title.trim().toLowerCase()])
    .filter(Boolean);

  if (known.includes('ceo')) return 'ceo';
  if (known.includes('manager')) return 'manager';
  if (known.includes('support')) return 'support';
  if (known.length > 0) return known[0];

  if (!warnedAboutRole) {
    warnedAboutRole = true;
    console.warn(
      `[Unit1 Admin] Неизвестные роли из /init: ${titles.join(', ') || 'пусто'}. Доступ не выдан.`,
    );
  }

  return 'client';
}

function toTrustedRole(role: UserRole): TrustedAdminRole | null {
  return role === 'ceo' || role === 'manager' || role === 'support' ? role : null;
}

function mapIdentity(raw: RawUser | undefined): InitIdentity {
  const user = mapUser(raw ?? {});
  const titles = (raw?.roles ?? []).map((role) => role.title ?? '').filter(Boolean);
  const role = mapRole(titles);

  const firstName = raw?.name?.trim() ?? '';
  const lastName = raw?.surname?.trim() ?? '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    id: user.id,
    name: displayName || user.phone || 'Admin',
    firstName,
    lastName,
    phoneNumber: raw?.phone_number ?? null,
    rawRoles: titles,
    role,
    trustedRole: toTrustedRole(role),
    balance: typeof raw?.wallet?.balance === 'number' ? raw.wallet.balance : null,
  };
}

export async function fetchInitData(signal?: AbortSignal): Promise<InitData> {
  const raw = await apiGet<RawInitResponse>(adminApi.url(adminApi.endpoints.init), signal);

  return {
    identity: mapIdentity(raw.me),
    users: mapPage(raw.users, mapUser),
  };
}
